import i18n from "@/i18n";
/**
 * WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — public shift report for external extras.
 * Opened from the restaurant's printed QR. No PIN, account or employee record.
 * The restaurant is resolved server-side from the opaque token only.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock3, Loader2, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";
import {
    buildExtraSubmission, extraErrorMessage, extraFormMissing, formatMinutes, isOvernight,
    newSubmissionId,
} from "@/components/workforce/externalExtrasHelpers";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/workforce/public/extra-form`;

function YesNo({ label, value, onChange, testid }) {
    return (
        <fieldset className="space-y-2" data-testid={testid}>
            <legend className="text-sm font-medium text-neutral-800">{label}</legend>
            <div className="grid grid-cols-2 gap-2">
                {[[true, "Yes", i18n.t("common.yes", "Yes")], [false, "No", i18n.t("common.no", "No")]].map(([v, t, shown]) => (
                    <button key={t} type="button" onClick={() => onChange(v)}
                        data-testid={`${testid}-${t.toLowerCase()}`} aria-pressed={value === v}
                        className={`h-12 rounded-xl border text-base transition-colors ${value === v
                            ? "border-neutral-900 bg-neutral-900 text-white"
                            : "border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50"}`}>
                        {shown}
                    </button>
                ))}
            </div>
        </fieldset>
    );
}

function Field({ label, children }) {
    return (
        <label className="block space-y-1.5">
            <span className="text-sm font-medium text-neutral-800">{label}</span>
            {children}
        </label>
    );
}

function Receipt({ r, form, onAnother }) {
    return (
        <div className="space-y-5 text-center" data-testid="extra-form-confirmation">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <div>
                <h1 className="text-2xl font-semibold text-neutral-900">{i18n.t("ui.externalShiftReport.reportSent", "Report sent")}</h1>
                <p className="mt-1 text-sm text-neutral-600">
                    {form.restaurant_name} will review your hours. Reference {r.report_ref}.
                </p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-4 text-left text-sm"
                data-testid="extra-form-confirmation-summary">
                <p className="font-medium text-neutral-900">{r.name}</p>
                <p className="text-neutral-700">
                    {formatYMDDate(r.shift_date)} · {r.start_time}–{r.end_time}
                    {r.end_date !== r.shift_date ? ` (ends ${formatYMDDate(r.end_date)})` : ""}
                </p>
                <p className="text-neutral-700">Reported time {formatMinutes(r.duration_minutes)}</p>
                {r.duplicate && (
                    <p className="mt-2 text-amber-700" data-testid="extra-form-duplicate-note">
                        This report had already been received; it was not sent twice.
                    </p>
                )}
            </div>
            <Button variant="outline" className="h-12 w-full" onClick={onAnother}
                data-testid="extra-form-another">{i18n.t("ui.externalShiftReport.reportAnotherShift", "Report another shift")}</Button>
        </div>
    );
}

export default function ExternalShiftReport() {
    const { token } = useParams();
    const [form, setForm] = useState(null);
    const [loadErr, setLoadErr] = useState("");
    const [f, setF] = useState({});
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const subId = useRef(newSubmissionId());
    const set = (p) => setF((x) => ({ ...x, ...p }));

    useEffect(() => {
        axios.get(`${BASE}/${encodeURIComponent(token)}`)
            .then(({ data }) => { setForm(data); setF({ shiftDate: data.today }); })
            .catch((e) => setLoadErr(extraErrorMessage(e, i18n.t("ui.externalShiftReport.couldNotOpenTheFormTry", "Could not open the form. Try again."))));
    }, [token]);

    const submit = useCallback(async () => {
        const missing = extraFormMissing(f);
        if (missing.length) { setErr(`Please add ${missing.join(", ")}.`); return; }
        setBusy(true); setErr("");
        try {
            const { data } = await axios.post(`${BASE}/${encodeURIComponent(token)}/reports`,
                buildExtraSubmission(f, subId.current));
            setReceipt(data);
        } catch (e) {
            setErr(extraErrorMessage(e));
        } finally {
            setBusy(false);
        }
    }, [f, token]);

    const another = () => {
        subId.current = newSubmissionId();
        setReceipt(null);
        setF({ shiftDate: form.today, name: f.name, agency: f.agency });
    };

    return (
        <main className="min-h-screen bg-[#f6f4ef] px-4 py-8" data-testid="extra-form-page">
            <div className="mx-auto max-w-md">
                {loadErr && <p className="rounded-xl bg-white p-5 text-neutral-800"
                    data-testid="extra-form-unavailable">{loadErr}</p>}
                {!form && !loadErr && <Loader2 className="mx-auto h-6 w-6 animate-spin text-neutral-500" />}
                {form && receipt && <Receipt r={receipt} form={form} onAnother={another} />}
                {form && !receipt && (
                    <div className="space-y-5">
                        <header>
                            <p className="text-xs uppercase tracking-wider text-neutral-500">{i18n.t("ui.externalShiftReport.shiftReport", "Shift report")}</p>
                            <h1 className="text-2xl font-semibold text-neutral-900"
                                data-testid="extra-form-restaurant">{form.restaurant_name}</h1>
                            <p className="mt-1 text-sm text-neutral-600">
                                {i18n.t("ui.externalShiftReport.forExtrasFromAnAgencyOr", "For extras from an agency or another company. Send this when your shift ends.")}
                            </p>
                        </header>
                        <Field label={i18n.t("ui.externalShiftReport.yourName", "Your name")}>
                            <Input className="h-12 text-base" autoComplete="name" value={f.name || ""}
                                data-testid="extra-form-name" onChange={(e) => set({ name: e.target.value })} />
                        </Field>
                        <Field label={i18n.t("ui.externalShiftReport.agencyOrCompanyYouWorkFor", "Agency or company you work for")}>
                            <Input className="h-12 text-base" value={f.agency || ""} data-testid="extra-form-agency"
                                onChange={(e) => set({ agency: e.target.value })} />
                        </Field>
                        <Field label={i18n.t("ui.externalShiftReport.shiftDateTheDayTheShift", "Shift date (the day the shift started)")}>
                            <Input type="date" className="h-12 text-base" max={form.today} value={f.shiftDate || ""}
                                data-testid="extra-form-date" onChange={(e) => set({ shiftDate: e.target.value })} />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label={i18n.t("ui.externalShiftReport.started", "Started")}>
                                <Input type="time" className="h-12 text-base" value={f.startTime || ""}
                                    data-testid="extra-form-start" onChange={(e) => set({ startTime: e.target.value })} />
                            </Field>
                            <Field label={i18n.t("ui.externalShiftReport.finished", "Finished")}>
                                <Input type="time" className="h-12 text-base" value={f.endTime || ""}
                                    data-testid="extra-form-end" onChange={(e) => set({ endTime: e.target.value })} />
                            </Field>
                        </div>
                        {isOvernight(f.startTime, f.endTime) && (
                            <p className="flex items-center gap-2 text-sm text-neutral-700" data-testid="extra-form-overnight">
                                <Moon className="h-4 w-4" /> {i18n.t("ui.externalShiftReport.endsTheNextDay", "Ends the next day.")}
                            </p>
                        )}
                        <YesNo label={i18n.t("ui.externalShiftReport.didYouEatAStaffMeal", "Did you eat a staff meal?")} value={f.staffMeal}
                            onChange={(v) => set({ staffMeal: v })} testid="extra-form-meal" />
                        <YesNo label={i18n.t("ui.externalShiftReport.didYouFinishAtTheExpected", "Did you finish at the expected time?")} value={f.finishedAsExpected}
                            onChange={(v) => set({ finishedAsExpected: v })} testid="extra-form-finished" />
                        <p className="flex items-start gap-2 text-xs text-neutral-500">
                            <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            {i18n.t("ui.externalShiftReport.enterTheTimesYouActuallyWorked", "Enter the times you actually worked. The restaurant reviews every report.")}
                        </p>
                        {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
                            data-testid="extra-form-error">{err}</p>}
                        <Button className="h-12 w-full text-base" disabled={busy} onClick={submit}
                            data-testid="extra-form-submit">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : i18n.t("ui.externalShiftReport.sendReport", "Send report")}
                        </Button>
                    </div>
                )}
            </div>
        </main>
    );
}
